import { Container, Graphics } from 'pixi.js';
import { TILE_SIZE } from '@/sprites/tiles';

/**
 * The tile the held tool will act on.
 *
 * Without it, aiming is guesswork: Katie's sprite is taller than a tile and
 * stands half over the row below her, so "the square in front of me" is not
 * something the player can read off the screen. Tilling the wrong tile is
 * cheap to undo but expensive to notice.
 *
 * Deliberately *not* a validity indicator. Whether a tile can be tilled,
 * planted or watered is decided by `useHeldItem`, and a second copy of those
 * rules here would drift out of step and start lying about what the button
 * does. This says only "this is the square you are pointing at", which is the
 * one thing it can never get wrong.
 *
 * It lives above the ambient tint rather than inside the world container, so
 * it stays legible at night and in a storm while the world darkens around it.
 * That costs it camera parenting, hence the explicit offset in `update`.
 */

/** Inset so the 1px stroke lands inside the tile rather than straddling it. */
const OUTLINE_COLOUR = 0xf2c66d;

export class TileCursor {
  readonly container = new Container();
  private readonly outline = new Graphics();

  constructor() {
    // Geometry is built once and moved, rather than recleared every frame:
    // the shape never changes, only where it sits.
    this.outline
      .rect(0.5, 0.5, TILE_SIZE - 1, TILE_SIZE - 1)
      .stroke({ width: 1, color: OUTLINE_COLOUR, alpha: 0.85, alignment: 0 });

    this.container.addChild(this.outline);
    this.container.visible = false;
  }

  /**
   * @param tileX  Target tile, in tiles.
   * @param tileY  Target tile, in tiles.
   * @param cameraX Camera position in world pixels, since this is not a child
   *                of the world container.
   * @param cameraY Camera position in world pixels.
   * @param visible False whenever nothing in hand acts on a tile — an empty
   *                cursor pointing at nothing is worse than no cursor.
   */
  update(
    tileX: number,
    tileY: number,
    cameraX: number,
    cameraY: number,
    visible: boolean,
  ): void {
    this.container.visible = visible;
    if (!visible) return;
    this.container.position.set(tileX * TILE_SIZE - cameraX, tileY * TILE_SIZE - cameraY);
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
